import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import * as serviceActions from '../../actions/service';
import { axiosCaller } from '../../utils/apiCaller';
import MikaPageHeader from '../../components/MikaPageHeader/MikaPageHeader';
import MikaConfirmModal from '../../components/MikaConfirmModal/MikaConfirmModal';
import { SERVICING_PRIORITY_COLORS } from '../../constants/constants';
import { toast } from 'react-toastify';
import '../Contact/Contact.scss';
import '../Transport/Transport.scss';
import '../Installation/Installation.scss';

var parseCoords = function(loc: string | null) {
    if (!loc) return null;
    var parts = loc.split(',').map(function(s) { return parseFloat(s.trim()); });
    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) return parts;
    return null;
};

var inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d0dbe8',
    fontSize: 13, outline: 'none', boxSizing: 'border-box',
};

var selectStyle: React.CSSProperties = {
    width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d0dbe8',
    fontSize: 13, outline: 'none', background: '#fff', color: '#0f172a',
};

interface Props {
    projectsReadyForService: any[];
    actions: any;
    history: any;
    loading: boolean;
}

var ServicingForm: React.FC<Props> = function(props) {
    var intl = useIntl();
    var fm = function(id: string, defaultMessage: string, values?: Record<string, any>) {
        return intl.formatMessage({ id: id, defaultMessage: defaultMessage }, values);
    };

    // Translate seeded servicing type names; admin-added types fall back to raw name.
    var translateTypeName = function(name: string | null | undefined): string {
        if (!name) return '';
        var n = name.toLowerCase().trim();
        if (n === 'kontroll') return fm('service.type.kontroll', 'Kontroll');
        if (n.indexOf('thjesht') !== -1) return fm('service.type.servisimSimple', 'Servisim i thjeshtë');
        if (n.indexOf('komplikuar') !== -1) return fm('service.type.servisimComplex', 'Servisim i komplikuar');
        return name;
    };

    // Toggle
    var _nr = useState(false); var isNonRegistered = _nr[0]; var setIsNonRegistered = _nr[1];

    // Project-based
    var _proj = useState<string>(''); var selectedProjectId = _proj[0]; var setSelectedProjectId = _proj[1];

    // Non-registered client fields
    var _fn = useState(''); var firstname = _fn[0]; var setFirstname = _fn[1];
    var _ln = useState(''); var lastname = _ln[0]; var setLastname = _ln[1];
    var _ph = useState(''); var phone = _ph[0]; var setPhone = _ph[1];
    var _em = useState(''); var email = _em[0]; var setEmail = _em[1];
    var _ad = useState(''); var address = _ad[0]; var setAddress = _ad[1];
    var _ct = useState(''); var city = _ct[0]; var setCity = _ct[1];
    var _co = useState(''); var country = _co[0]; var setCountry = _co[1];

    // Service fields
    var _ty = useState<string>(''); var servicingTypeId = _ty[0]; var setServicingTypeId = _ty[1];
    var _pr = useState<number>(2); var priority = _pr[0]; var setPriority = _pr[1];
    var _wa = useState(false); var isWarranty = _wa[0]; var setIsWarranty = _wa[1];
    var _we = useState(''); var warrantyExpiry = _we[0]; var setWarrantyExpiry = _we[1];
    var _di = useState(''); var diagnosis = _di[0]; var setDiagnosis = _di[1];
    var _lo = useState(''); var servicingLocation = _lo[0]; var setServicingLocation = _lo[1];
    var _cm = useState<string[]>([]); var comments = _cm[0]; var setComments = _cm[1];
    var _nc = useState(''); var newComment = _nc[0]; var setNewComment = _nc[1];

    // Product selection
    var _ap = useState<any[]>([]); var availableProducts = _ap[0]; var setAvailableProducts = _ap[1];
    var _sp = useState<number[]>([]); var selectedProductIds = _sp[0]; var setSelectedProductIds = _sp[1];
    var _aa = useState<any[]>([]); var availableArticles = _aa[0]; var setAvailableArticles = _aa[1];
    var _sa = useState<number[]>([]); var selectedArticleIds = _sa[0]; var setSelectedArticleIds = _sa[1];
    var _as = useState(''); var articleSearch = _as[0]; var setArticleSearch = _as[1];

    // Service history
    var _hist = useState<any[]>([]); var serviceHistory = _hist[0]; var setServiceHistory = _hist[1];
    var _histOpen = useState(false); var historyOpen = _histOpen[0]; var setHistoryOpen = _histOpen[1];
    var _pt = useState<any[]>([]); var productTickets = _pt[0]; var setProductTickets = _pt[1];
    var _dup = useState(false); var dupConfirm = _dup[0]; var setDupConfirm = _dup[1];

    // File uploads
    var _files = useState<Array<{ file: File; category: string; preview: string }>>([]); var pendingFiles = _files[0]; var setPendingFiles = _files[1];
    var _fileCat = useState<string>('before'); var activeFileCategory = _fileCat[0]; var setActiveFileCategory = _fileCat[1];

    // Data
    var _types = useState<any[]>([]); var servicingTypes = _types[0]; var setServicingTypes = _types[1];
    var _saving = useState(false); var saving = _saving[0]; var setSaving = _saving[1];

    // Derived: selected project with client info
    var selectedProject = (props.projectsReadyForService || []).find(function(p: any) { return String(p.id) === selectedProjectId; }) || null;
    var clientInfo = selectedProject?.client || null;
    var measureLocation = selectedProject?.measureLocation || null;

    useEffect(function() {
        props.actions.fetchServiceableProjects();
        axiosCaller('GET', 'servicing-types').then(function(res: any) {
            setServicingTypes(res?.data || []);
        });
        // Load articles for non-registered client product selection
        axiosCaller('GET', 'articles').then(function(res: any) {
            setAvailableArticles(res?.data?.data || res?.data || []);
        });
    }, []);

    // Load products + service history when project changes
    useEffect(function() {
        if (selectedProjectId) {
            axiosCaller('GET', 'project/' + selectedProjectId + '/products').then(function(res: any) {
                var products = res?.data?.data?.products || [];
                setAvailableProducts(products);
                setSelectedProductIds([]);
            });
            props.actions.fetchServiceHistory(parseInt(selectedProjectId)).then(function(data: any) {
                setServiceHistory(data || []);
                setHistoryOpen(false);
            });
            props.actions.fetchProductTickets(parseInt(selectedProjectId)).then(function(data: any) {
                setProductTickets(data || []);
            });
        } else {
            setAvailableProducts([]);
            setSelectedProductIds([]);
            setServiceHistory([]);
            setHistoryOpen(false);
            setProductTickets([]);
        }
    }, [selectedProjectId]);

    var addFiles = function(e: React.ChangeEvent<HTMLInputElement>) {
        var fileList = e.target.files;
        if (!fileList) return;
        var newFiles: Array<{ file: File; category: string; preview: string }> = [];
        for (var i = 0; i < fileList.length; i++) {
            newFiles.push({
                file: fileList[i],
                category: activeFileCategory,
                preview: URL.createObjectURL(fileList[i]),
            });
        }
        setPendingFiles(function(prev) { return prev.concat(newFiles); });
        e.target.value = '';
    };

    var removeFile = function(idx: number) {
        setPendingFiles(function(prev) {
            URL.revokeObjectURL(prev[idx].preview);
            return prev.filter(function(_, i) { return i !== idx; });
        });
    };

    var uploadPendingFiles = function(servicingId: number) {
        if (pendingFiles.length === 0) return Promise.resolve();

        var promises = pendingFiles.map(function(pf) {
            var formData = new FormData();
            formData.append('servicing_files', pf.file);
            formData.append('file_category', pf.category);
            return props.actions.uploadServicingFiles(servicingId, formData);
        });

        return Promise.all(promises);
    };

    var addComment = function() {
        if (newComment.trim()) {
            setComments(function(prev) { return prev.concat([newComment.trim()]); });
            setNewComment('');
        }
    };

    var removeComment = function(idx: number) {
        setComments(function(prev) { return prev.filter(function(_, i) { return i !== idx; }); });
    };

    var handleSubmit = function() {
        if (!diagnosis.trim()) { toast.error(fm('service.form.toast.diagnosisRequired', 'Diagnostika është e detyrueshme')); return; }
        if (!isNonRegistered && !selectedProjectId) { toast.error(fm('service.form.toast.selectProject', 'Zgjidhni një projekt')); return; }
        if (isNonRegistered) {
            if (!firstname.trim()) { toast.error(fm('service.form.toast.firstnameRequired', 'Emri është i detyrueshëm')); return; }
            if (!lastname.trim()) { toast.error(fm('service.form.toast.lastnameRequired', 'Mbiemri është i detyrueshëm')); return; }
            if (!phone.trim()) { toast.error(fm('service.form.toast.phoneRequired', 'Telefoni është i detyrueshëm')); return; }
            if (!city.trim()) { toast.error(fm('service.form.toast.cityRequired', 'Qyteti është i detyrueshëm')); return; }
            if (!country.trim()) { toast.error(fm('service.form.toast.countryRequired', 'Shteti është i detyrueshëm')); return; }
        }

        // Soft-warn (not block) if a selected product already has an open ticket
        if (!isNonRegistered) {
            var hasOpenDuplicate = selectedProductIds.some(function(id) {
                var t = ticketsForProduct(id);
                return t && t.open > 0;
            });
            if (hasOpenDuplicate) { setDupConfirm(true); return; }
        }

        submitNow();
    };

    var ticketsForProduct = function(productId: number) {
        return (productTickets || []).find(function(t: any) { return t.productId === productId; });
    };

    var submitNow = function() {
        setSaving(true);

        var payload: any = {
            servicing_type_id: servicingTypeId ? parseInt(servicingTypeId) : undefined,
            priority: priority,
            is_warranty: isWarranty ? 1 : 0,
            warranty_expiry_date: isWarranty && warrantyExpiry ? warrantyExpiry : undefined,
            diagnosis: diagnosis.trim(),
            servicing_location: servicingLocation.trim() || undefined,
            comments: comments.length > 0 ? comments : undefined,
        };

        if (!isNonRegistered) {
            payload.project_id = parseInt(selectedProjectId);
            if (selectedProductIds.length > 0) payload.product_ids = selectedProductIds;
        } else {
            payload.firstname = firstname.trim() || undefined;
            payload.lastname = lastname.trim() || undefined;
            payload.phone_number = phone.trim() || undefined;
            payload.email = email.trim() || undefined;
            payload.address = address.trim() || undefined;
            payload.city = city.trim() || undefined;
            payload.country = country.trim() || undefined;
            if (selectedArticleIds.length > 0) payload.article_ids = selectedArticleIds;
        }

        props.actions.createNewServicing(payload).then(function(result: any) {
            var servicingId = result?.data?.id;
            if (servicingId && pendingFiles.length > 0) {
                uploadPendingFiles(servicingId).then(function() {
                    toast.success(fm('service.form.toast.created', 'Servisimi u krijua'));
                    props.history.push('/dashboard/service/' + servicingId);
                    setSaving(false);
                }).catch(function() {
                    toast.success(fm('service.form.toast.createdFilesFailed', 'Servisimi u krijua, por fotografitë nuk u ngarkuan'));
                    props.history.push('/dashboard/service/' + servicingId);
                    setSaving(false);
                });
            } else if (servicingId) {
                toast.success(fm('service.form.toast.created', 'Servisimi u krijua'));
                props.history.push('/dashboard/service/' + servicingId);
                setSaving(false);
            } else {
                toast.success(fm('service.form.toast.created', 'Servisimi u krijua'));
                props.history.push('/dashboard/service');
                setSaving(false);
            }
        }).catch(function() {
            toast.error(fm('service.form.toast.somethingWrong', 'Diçka shkoi gabim'));
            setSaving(false);
        });
    };

    var priorityOptions = [
        { value: 1, label: fm('service.priority.low', 'E ulët'), color: '#94a3b8', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg> },
        { value: 2, label: fm('service.priority.normal', 'Normale'), color: '#3b82f6', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"/></svg> },
        { value: 3, label: fm('service.priority.high', 'E lartë'), color: '#f97316', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="18 15 12 9 6 15"/></svg> },
        { value: 4, label: fm('service.priority.urgent', 'Urgjente'), color: '#ef4444', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="18 15 12 9 6 15"/><polyline points="18 20 12 14 6 20"/></svg> },
    ];

    // Searchable project filter
    var _ps = useState(''); var projectSearch = _ps[0]; var setProjectSearch = _ps[1];
    var filteredProjects = (props.projectsReadyForService || []).filter(function(p: any) {
        if (!projectSearch.trim()) return true;
        var search = projectSearch.toLowerCase();
        var pName = (p.projectName || p.name || '').toLowerCase();
        var cName = p.client ? ((p.client.firstnameAndLastname || '') + ' ' + (p.client.lastName || '')).toLowerCase() : '';
        return pName.indexOf(search) !== -1 || cName.indexOf(search) !== -1;
    });

    return (
        <div className="clp-page" style={{ gap: 12 }}>
            <MikaPageHeader
                title={fm('service.form.createTitle', 'Krijo Servisim të Ri')}
                actions={
                    <button onClick={handleSubmit} disabled={saving}
                        style={{
                            padding: '7px 18px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.25)',
                            background: 'rgba(255,255,255,0.12)', color: '#fff', fontSize: 12, fontWeight: 500,
                            cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.5 : 1,
                            display: 'flex', alignItems: 'center', gap: 6, transition: 'background 0.15s',
                        }}
                        onMouseEnter={function(e) { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.22)'; }}
                        onMouseLeave={function(e) { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.12)'; }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                        {saving ? fm('service.form.saving', 'Duke ruajtur...') : fm('service.form.createAction', 'Krijo servisimin')}
                    </button>
                }
            />

            <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 16, padding: '12px 16px' }}>

                {/* Non-registered toggle */}
                <div style={{
                    background: isNonRegistered ? '#fff7ed' : '#fff',
                    border: '1px solid ' + (isNonRegistered ? '#fed7aa' : '#e2e8f0'),
                    borderRadius: 8, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10,
                    transition: 'all 0.2s',
                }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#0f172a', fontWeight: 500 }}>
                        <input type="checkbox" checked={isNonRegistered}
                            onChange={function(e) { setIsNonRegistered(e.target.checked); setSelectedProjectId(''); }}
                            style={{ width: 16, height: 16 } as any} />
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={isNonRegistered ? '#f97316' : '#94a3b8'} strokeWidth="2"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>
                        <FormattedMessage id="service.form.nonRegisteredLabel" defaultMessage="Klient i pa-regjistruar (para Mika)" />
                    </label>
                </div>

                {/* Two-column layout */}
                <div className="inst-detail-main">
                <div className="inst-detail-content">

                {/* Client/Project section */}
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                    <div className="t-card-header">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                        {isNonRegistered ? fm('service.form.clientDataHeader', 'Të dhënat e klientit') : fm('service.form.projectHeader', 'Projekti')}
                    </div>
                    <div className="t-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {!isNonRegistered ? (
                            <>
                                <div>
                                    <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                                        <FormattedMessage id="service.form.projectRequired" defaultMessage="Projekti" /> <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    {selectedProject ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6 }}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
                                            <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#1e3a5f' }}>{selectedProject.projectName || selectedProject.name}</span>
                                            <button onClick={function() { setSelectedProjectId(''); setProjectSearch(''); }}
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 2 }}
                                                onMouseEnter={function(e: any) { e.currentTarget.style.color = '#ef4444'; }}
                                                onMouseLeave={function(e: any) { e.currentTarget.style.color = '#94a3b8'; }}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                            </button>
                                        </div>
                                    ) : (
                                        <div>
                                            <div style={{ position: 'relative', marginBottom: 4 }}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"
                                                    style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }}>
                                                    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                                                </svg>
                                                <input type="text" value={projectSearch} onChange={function(e) { setProjectSearch(e.target.value); }}
                                                    placeholder={fm('service.form.projectSearch', 'Kërko projektin ose klientin...')}
                                                    style={{ ...inputStyle, paddingLeft: 32 }} />
                                            </div>
                                            <div style={{ maxHeight: 200, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff' }}>
                                                {filteredProjects.length === 0 && (
                                                    <div style={{ padding: '12px 14px', fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>{fm('service.form.noProjectsFound', 'Nuk u gjet asnjë projekt')}</div>
                                                )}
                                                {filteredProjects.map(function(p: any) {
                                                    var clientName = p.client ? (p.client.firstnameAndLastname || p.client.firstname_and_lastname || '') + ' ' + (p.client.lastName || p.client.last_name || '') : '';
                                                    return (
                                                        <div key={p.id} onClick={function() { setSelectedProjectId(String(p.id)); setProjectSearch(''); }}
                                                            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', transition: 'background 0.1s' }}
                                                            onMouseEnter={function(e: any) { e.currentTarget.style.background = '#f0f9ff'; }}
                                                            onMouseLeave={function(e: any) { e.currentTarget.style.background = 'transparent'; }}>
                                                            <div style={{ flex: 1 }}>
                                                                <div style={{ fontSize: 13, fontWeight: 500, color: '#0f172a' }}>{p.projectName || p.name || p.label}</div>
                                                                <div style={{ fontSize: 11, color: '#64748b' }}>{clientName.trim()}</div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Client info card — appears when project is selected */}
                                {clientInfo && (
                                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px', marginTop: 4 }}>
                                        <div className="t-delivery-info">
                                            <div className="t-delivery-field">
                                                <div className="t-delivery-label"><FormattedMessage id="service.form.fieldClient" defaultMessage="Klienti" /></div>
                                                <div className="t-delivery-value">{clientInfo.name || ((clientInfo.firstnameAndLastname || '') + ' ' + (clientInfo.lastName || '')).trim() || '—'}</div>
                                            </div>
                                            <div className="t-delivery-field">
                                                <div className="t-delivery-label"><FormattedMessage id="service.form.fieldPhone" defaultMessage="Telefoni" /></div>
                                                <div className="t-delivery-value">{clientInfo.phoneNumber || clientInfo.phone_number || <span className="t-delivery-value-empty">—</span>}</div>
                                            </div>
                                            <div className="t-delivery-field">
                                                <div className="t-delivery-label"><FormattedMessage id="service.form.fieldCity" defaultMessage="Qyteti" /></div>
                                                <div className="t-delivery-value">{clientInfo.city || <span className="t-delivery-value-empty">—</span>}</div>
                                            </div>
                                            <div className="t-delivery-field">
                                                <div className="t-delivery-label"><FormattedMessage id="service.form.fieldCountry" defaultMessage="Shteti" /></div>
                                                <div className="t-delivery-value">{clientInfo.country || <span className="t-delivery-value-empty">—</span>}</div>
                                            </div>
                                            <div className="t-delivery-field">
                                                <div className="t-delivery-label"><FormattedMessage id="service.form.fieldAddress" defaultMessage="Adresa" /></div>
                                                <div className="t-delivery-value">{clientInfo.address || <span className="t-delivery-value-empty">—</span>}</div>
                                            </div>
                                            {measureLocation && parseCoords(measureLocation) && (
                                                <div className="t-delivery-field">
                                                    <div className="t-delivery-label"><FormattedMessage id="service.form.fieldLocation" defaultMessage="Lokacioni" /></div>
                                                    <div className="t-delivery-value">
                                                        <a href={'https://www.google.com/maps/dir/?api=1&destination=' + parseCoords(measureLocation)![0] + ',' + parseCoords(measureLocation)![1]}
                                                            target="_blank" rel="noopener noreferrer" className="t-maps-link">
                                                            <svg width="11" height="14" viewBox="3 1 18 21" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                                                            {fm('service.form.googleMaps', 'Google Maps')}
                                                        </a>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Service history for this project */}
                                {serviceHistory.length > 0 && (
                                    <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, overflow: 'hidden', marginTop: 4 }}>
                                        <div onClick={function() { setHistoryOpen(!historyOpen); }}
                                            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', cursor: 'pointer', userSelect: 'none' as any }}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                                            <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: '#92400e' }}>
                                                {fm('service.form.historyNote', 'Ky projekt ka {count} servisime të mëparshme', { count: serviceHistory.length })}
                                            </span>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2"
                                                style={{ transition: 'transform 0.2s', transform: historyOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                                                <polyline points="6 9 12 15 18 9" />
                                            </svg>
                                        </div>
                                        {historyOpen && (
                                            <div style={{ padding: '0 14px 10px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                {serviceHistory.map(function(h: any) {
                                                    var statusLabels: Record<number, string> = {
                                                        1: fm('service.strip.registered', 'E regjistruar'),
                                                        2: fm('service.strip.diagnosing', 'Në diagnostikim'),
                                                        3: fm('service.map.waitingMaterial', 'Në pritje të materialit'),
                                                        4: fm('service.strip.repairing', 'Në riparim'),
                                                        5: fm('service.map.waitingClient', 'Në pritje të klientit'),
                                                        6: fm('service.strip.completed', 'E përfunduar'),
                                                    };
                                                    var statusColors: Record<number, { color: string; bg: string }> = { 1: { color: '#94a3b8', bg: '#f1f5f9' }, 2: { color: '#3b82f6', bg: '#dbeafe' }, 3: { color: '#f59e0b', bg: '#fef3c7' }, 4: { color: '#8b5cf6', bg: '#ede9fe' }, 5: { color: '#f97316', bg: '#fff7ed' }, 6: { color: '#22c55e', bg: '#dcfce7' } };
                                                    var priorityColors: Record<number, { color: string; bg: string }> = { 1: { color: '#94a3b8', bg: '#f1f5f9' }, 2: { color: '#3b82f6', bg: '#dbeafe' }, 3: { color: '#f97316', bg: '#fff7ed' }, 4: { color: '#ef4444', bg: '#fef2f2' } };
                                                    var priorityLabels: Record<number, string> = {
                                                        1: fm('service.priority.low', 'E ulët'),
                                                        2: fm('service.priority.normal', 'Normale'),
                                                        3: fm('service.priority.high', 'E lartë'),
                                                        4: fm('service.priority.urgent', 'Urgjente'),
                                                    };
                                                    var sc = statusColors[h.status] || { color: '#94a3b8', bg: '#f1f5f9' };
                                                    var pc = priorityColors[h.priority] || { color: '#3b82f6', bg: '#dbeafe' };
                                                    var dt = h.created_at ? new Date(h.created_at) : null;
                                                    var dateStr = dt && !isNaN(dt.getTime()) ? String(dt.getDate()).padStart(2,'0') + '/' + String(dt.getMonth()+1).padStart(2,'0') + '/' + dt.getFullYear() : '';
                                                    return (
                                                        <a key={h.id} href={'/dashboard/service/' + h.id} target="_blank" rel="noopener noreferrer"
                                                            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: '#fff', borderRadius: 6, border: '1px solid #f1f5f9', textDecoration: 'none', transition: 'border-color 0.15s' }}
                                                            onMouseEnter={function(e: any) { e.currentTarget.style.borderColor = '#d97706'; }}
                                                            onMouseLeave={function(e: any) { e.currentTarget.style.borderColor = '#f1f5f9'; }}>
                                                            <span style={{ fontSize: 11, color: '#64748b', minWidth: 70 }}>{dateStr}</span>
                                                            <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 9, fontWeight: 600, color: sc.color, background: sc.bg }}>
                                                                {statusLabels[h.status] || '?'}
                                                            </span>
                                                            <span style={{ padding: '2px 6px', borderRadius: 10, fontSize: 9, fontWeight: 600, color: pc.color, background: pc.bg }}>
                                                                {priorityLabels[h.priority] || '?'}
                                                            </span>
                                                            {h.servicing_type && <span style={{ fontSize: 10, color: '#64748b' }}>{translateTypeName(h.servicing_type.name)}</span>}
                                                            <span style={{ flex: 1, fontSize: 11, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as any }}>
                                                                {h.diagnosis ? (h.diagnosis.length > 40 ? h.diagnosis.substring(0, 40) + '...' : h.diagnosis) : ''}
                                                            </span>
                                                        </a>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                    <div>
                                        <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}><FormattedMessage id="service.form.firstname" defaultMessage="Emri" /> <span style={{ color: '#ef4444' }}>*</span></label>
                                        <input style={inputStyle} placeholder={fm('service.form.firstnamePlaceholder', 'Emri...')} value={firstname} onChange={function(e) { setFirstname(e.target.value); }} />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}><FormattedMessage id="service.form.lastname" defaultMessage="Mbiemri" /> <span style={{ color: '#ef4444' }}>*</span></label>
                                        <input style={inputStyle} placeholder={fm('service.form.lastnamePlaceholder', 'Mbiemri...')} value={lastname} onChange={function(e) { setLastname(e.target.value); }} />
                                    </div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                    <div>
                                        <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}><FormattedMessage id="service.form.fieldPhone" defaultMessage="Telefoni" /> <span style={{ color: '#ef4444' }}>*</span></label>
                                        <input style={inputStyle} placeholder={fm('service.form.phonePlaceholder', 'Nr. telefoni...')} value={phone} onChange={function(e) { setPhone(e.target.value); }} />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}><FormattedMessage id="service.form.email" defaultMessage="Email" /></label>
                                        <input style={inputStyle} placeholder={fm('service.form.emailPlaceholder', 'Email...')} value={email} onChange={function(e) { setEmail(e.target.value); }} />
                                    </div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                                    <div>
                                        <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}><FormattedMessage id="service.form.fieldAddress" defaultMessage="Adresa" /></label>
                                        <input style={inputStyle} placeholder={fm('service.form.addressPlaceholder', 'Adresa...')} value={address} onChange={function(e) { setAddress(e.target.value); }} />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}><FormattedMessage id="service.form.fieldCity" defaultMessage="Qyteti" /> <span style={{ color: '#ef4444' }}>*</span></label>
                                        <input style={inputStyle} placeholder={fm('service.form.cityPlaceholder', 'Qyteti...')} value={city} onChange={function(e) { setCity(e.target.value); }} />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}><FormattedMessage id="service.form.fieldCountry" defaultMessage="Shteti" /> <span style={{ color: '#ef4444' }}>*</span></label>
                                        <input style={inputStyle} placeholder={fm('service.form.countryPlaceholder', 'Shteti...')} value={country} onChange={function(e) { setCountry(e.target.value); }} />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Product selection */}
                {!isNonRegistered && availableProducts.length > 0 && (
                    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                        <div className="t-card-header">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
                            {fm('service.form.productsHeader', 'Produktet ({selected}/{total})', { selected: selectedProductIds.length, total: availableProducts.length })}
                        </div>
                        <div className="t-card-body" style={{ padding: 0 }}>
                            {availableProducts.map(function(p: any) {
                                var isChecked = selectedProductIds.indexOf(p.id) !== -1;
                                return (
                                    <label key={p.id} style={{
                                        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                                        borderBottom: '1px solid #f3f4f6', cursor: 'pointer',
                                        background: isChecked ? '#eff6ff' : 'transparent',
                                        borderLeft: isChecked ? '3px solid #3b82f6' : '3px solid transparent',
                                        transition: 'all 0.15s',
                                    }}>
                                        <input type="checkbox" checked={isChecked}
                                            onChange={function() {
                                                setSelectedProductIds(function(prev) {
                                                    return isChecked ? prev.filter(function(id) { return id !== p.id; }) : prev.concat([p.id]);
                                                });
                                            }}
                                            style={{ width: 16, height: 16, flexShrink: 0 } as any} />
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: 13, fontWeight: 500, color: '#0f172a' }}>{p.name || '—'}</div>
                                            {p.code && <span style={{ fontSize: 10, color: '#94a3b8', fontFamily: 'monospace' }}>{p.code}</span>}
                                        </div>
                                        {(function() {
                                            var t = ticketsForProduct(p.id);
                                            if (!t) return null;
                                            return (
                                                <span style={{ display: 'flex', gap: 4, marginRight: 6 }}>
                                                    {t.open > 0 && <span style={{ padding: '2px 7px', borderRadius: 10, fontSize: 9, fontWeight: 700, color: '#b45309', background: '#fef3c7' }}>{fm('service.form.openTickets', '{count} hapur', { count: t.open })}</span>}
                                                    {t.closed > 0 && <span style={{ padding: '2px 7px', borderRadius: 10, fontSize: 9, fontWeight: 600, color: '#64748b', background: '#f1f5f9' }}>{fm('service.form.closedTickets', '{count} mbyllur', { count: t.closed })}</span>}
                                                </span>
                                            );
                                        })()}
                                        {p.unit?.name && <span style={{ fontSize: 11, color: '#64748b' }}>{p.quantity || ''} {p.unit.name}</span>}
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Article selection for non-registered clients */}
                {isNonRegistered && availableArticles.length > 0 && (
                    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                        <div className="t-card-header">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
                            {fm('service.form.articlesHeader', 'Artikujt ({count})', { count: selectedArticleIds.length })}
                        </div>
                        <div style={{ padding: '10px 14px 0' }}>
                            <input type="text" placeholder={fm('service.form.articleSearch', 'Kërko artikullin...')} value={articleSearch}
                                onChange={function(e) { setArticleSearch(e.target.value); }}
                                style={{ ...inputStyle, marginBottom: 8, fontSize: 12 }} />
                        </div>
                        <div style={{ maxHeight: 220, overflow: 'auto', padding: 0 }}>
                            {availableArticles.filter(function(a: any) {
                                if (!articleSearch.trim()) return true;
                                var s = articleSearch.toLowerCase();
                                return ((a.label || a.name || '').toLowerCase().indexOf(s) !== -1) || ((a.productCode || a.product_code || '').toLowerCase().indexOf(s) !== -1);
                            }).map(function(a: any) {
                                var articleId = a.value || a.id;
                                var articleName = a.label || a.name || '—';
                                var articleCode = a.productCode || a.product_code || '';
                                var isChecked = selectedArticleIds.indexOf(articleId) !== -1;
                                return (
                                    <label key={articleId} style={{
                                        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                                        borderBottom: '1px solid #f3f4f6', cursor: 'pointer',
                                        background: isChecked ? '#eff6ff' : 'transparent',
                                        borderLeft: isChecked ? '3px solid #3b82f6' : '3px solid transparent',
                                        transition: 'all 0.15s',
                                    }}>
                                        <input type="checkbox" checked={isChecked}
                                            onChange={function() {
                                                setSelectedArticleIds(function(prev) {
                                                    return isChecked ? prev.filter(function(id) { return id !== articleId; }) : prev.concat([articleId]);
                                                });
                                            }}
                                            style={{ width: 16, height: 16, flexShrink: 0 } as any} />
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: 13, fontWeight: 500, color: '#0f172a' }}>{articleName}</div>
                                            {articleCode && <span style={{ fontSize: 10, color: '#94a3b8', fontFamily: 'monospace' }}>{articleCode}</span>}
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Diagnosis & Location */}
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                    <div className="t-card-header">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                        <FormattedMessage id="service.form.diagnosisLocationHeader" defaultMessage="Diagnostika & Vendndodhja" />
                    </div>
                    <div className="t-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div>
                            <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                                <FormattedMessage id="service.form.diagnosisLabel" defaultMessage="Diagnostika" /> <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <textarea style={{ ...inputStyle, minHeight: 100, resize: 'vertical' } as React.CSSProperties}
                                placeholder={fm('service.form.diagnosisPlaceholder', 'Përshkruani problemin e raportuar nga klienti...')}
                                value={diagnosis} onChange={function(e) { setDiagnosis(e.target.value); }} />
                        </div>
                        <div>
                            <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}><FormattedMessage id="service.form.servicingLocation" defaultMessage="Vendndodhja e servisimit" /></label>
                            <input style={inputStyle} placeholder={fm('service.form.servicingLocationPlaceholder', 'Ku do të kryhet servisimi...')} value={servicingLocation}
                                onChange={function(e) { setServicingLocation(e.target.value); }} />
                        </div>
                    </div>
                </div>

                </div>{/* end inst-detail-content */}
                <div className="inst-detail-sidebar">

                {/* Service details */}
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                    <div className="t-card-header">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
                        <FormattedMessage id="service.form.detailsHeader" defaultMessage="Detajet e servisimit" />
                    </div>
                    <div className="t-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <div>
                                <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}><FormattedMessage id="service.form.servicingType" defaultMessage="Lloji i servisimit" /></label>
                                <select value={servicingTypeId} onChange={function(e) { setServicingTypeId(e.target.value); }} style={selectStyle}>
                                    <option value="">{fm('service.form.servicingTypePlaceholder', 'Zgjidh llojin...')}</option>
                                    {servicingTypes.map(function(t: any) {
                                        return <option key={t.id} value={t.id}>{translateTypeName(t.name)}</option>;
                                    })}
                                </select>
                            </div>
                            <div>
                                <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}><FormattedMessage id="service.form.priority" defaultMessage="Prioriteti" /></label>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                                    {priorityOptions.map(function(opt: any) {
                                        var isSelected = priority === opt.value;
                                        return (
                                            <button key={opt.value} onClick={function() { setPriority(opt.value); }}
                                                style={{
                                                    padding: '10px 8px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                                                    cursor: 'pointer', transition: 'all 0.2s', border: 'none',
                                                    background: isSelected ? opt.color : '#f1f5f9',
                                                    color: isSelected ? '#fff' : '#64748b',
                                                    boxShadow: isSelected ? '0 2px 8px ' + opt.color + '44' : 'none',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                                                }}>
                                                {opt.icon}
                                                {opt.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* Warranty */}
                        <div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#0f172a' }}>
                                <input type="checkbox" checked={isWarranty}
                                    onChange={function(e) { setIsWarranty(e.target.checked); }}
                                    style={{ width: 16, height: 16 } as any} />
                                <FormattedMessage id="service.form.warrantyToggle" defaultMessage="Nën garanci" />
                            </label>
                            {isWarranty && (
                                <div style={{ marginTop: 8 }}>
                                    <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}><FormattedMessage id="service.form.warrantyExpiry" defaultMessage="Data e skadimit" /></label>
                                    <input type="date" style={inputStyle} value={warrantyExpiry}
                                        onChange={function(e) { setWarrantyExpiry(e.target.value); }} />
                                </div>
                            )}
                        </div>

                    </div>
                </div>

                {/* File upload with categories */}
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                    <div className="t-card-header">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                        {fm('service.form.photosHeader', 'Fotografitë ({count})', { count: pendingFiles.length })}
                    </div>
                    <div className="t-card-body" style={{ padding: '12px 14px' }}>
                        {/* Category tabs */}
                        <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                            {[{ key: 'before', label: fm('service.form.categoryBefore', 'Para servisimit') }, { key: 'other', label: fm('service.form.categoryOther', 'Tjetër') }].map(function(cat) {
                                var isActive = activeFileCategory === cat.key;
                                var count = pendingFiles.filter(function(f) { return f.category === cat.key; }).length;
                                return (
                                    <button key={cat.key} onClick={function() { setActiveFileCategory(cat.key); }}
                                        style={{
                                            padding: '6px 14px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                                            border: 'none', cursor: 'pointer', transition: 'all 0.15s',
                                            background: isActive ? '#1e3a5f' : '#f1f5f9',
                                            color: isActive ? '#fff' : '#64748b',
                                        }}>
                                        {cat.label}{count > 0 ? ' (' + count + ')' : ''}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Upload button */}
                        <label style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                            padding: '14px 16px', borderRadius: 8, border: '2px dashed #d0dbe8',
                            cursor: 'pointer', background: '#fafbfc', transition: 'all 0.15s',
                            fontSize: 12, color: '#64748b', fontWeight: 500,
                        }}
                        onMouseEnter={function(e: any) { e.currentTarget.style.borderColor = '#1e3a5f'; e.currentTarget.style.color = '#1e3a5f'; }}
                        onMouseLeave={function(e: any) { e.currentTarget.style.borderColor = '#d0dbe8'; e.currentTarget.style.color = '#64748b'; }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                            {fm('service.form.uploadPhotos', 'Ngarko fotografi ({category})', { category: activeFileCategory === 'before' ? fm('service.form.categoryBefore', 'Para servisimit') : fm('service.form.categoryOther', 'Tjetër') })}
                            <input type="file" multiple accept="image/*" onChange={addFiles} style={{ display: 'none' }} />
                        </label>

                        {/* File preview list */}
                        {pendingFiles.length > 0 && (
                            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {pendingFiles.map(function(pf, idx) {
                                    var catLabel = pf.category === 'before' ? fm('service.form.categoryBefore', 'Para servisimit') : fm('service.form.categoryOther', 'Tjetër');
                                    return (
                                        <div key={idx} style={{
                                            display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px',
                                            background: '#f8fafc', borderRadius: 6, border: '1px solid #f1f5f9',
                                        }}>
                                            <img src={pf.preview} alt="" style={{ width: 32, height: 32, borderRadius: 4, objectFit: 'cover' }} />
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: 12, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as any }}>{pf.file.name}</div>
                                                <div style={{ fontSize: 10, color: '#94a3b8' }}>{catLabel}</div>
                                            </div>
                                            <button onClick={function() { removeFile(idx); }}
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 2 }}
                                                onMouseEnter={function(e: any) { e.currentTarget.style.color = '#ef4444'; }}
                                                onMouseLeave={function(e: any) { e.currentTarget.style.color = '#94a3b8'; }}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {/* Comments */}
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                    <div className="t-card-header">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                        {fm('service.form.commentsHeader', 'Komentet ({count})', { count: comments.length })}
                    </div>
                    <div className="t-card-body" style={{ padding: '10px 14px' }}>
                        <div style={{ display: 'flex', gap: 6, marginBottom: comments.length > 0 ? 10 : 0 }}>
                            <input type="text" value={newComment} onChange={function(e) { setNewComment(e.target.value); }}
                                onKeyDown={function(e) { if (e.key === 'Enter') addComment(); }}
                                placeholder={fm('service.form.commentPlaceholder', 'Shkruaj koment...')}
                                style={{ ...inputStyle, fontSize: 12 }} />
                            <button onClick={addComment} disabled={!newComment.trim()}
                                style={{
                                    padding: '6px 12px', borderRadius: 6, border: 'none',
                                    background: newComment.trim() ? '#1e3a5f' : '#e2e8f0',
                                    color: newComment.trim() ? '#fff' : '#94a3b8',
                                    fontSize: 11, fontWeight: 600, cursor: newComment.trim() ? 'pointer' : 'default',
                                    whiteSpace: 'nowrap' as any,
                                }}><FormattedMessage id="service.form.addComment" defaultMessage="Shto" /></button>
                        </div>
                        {comments.map(function(c, idx) {
                            return (
                                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #f3f4f6', fontSize: 12 }}>
                                    <span style={{ flex: 1, color: '#0f172a' }}>{c}</span>
                                    <button onClick={function() { removeComment(idx); }}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 2 }}
                                        onMouseEnter={function(e: any) { e.currentTarget.style.color = '#ef4444'; }}
                                        onMouseLeave={function(e: any) { e.currentTarget.style.color = '#94a3b8'; }}>
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>

                </div>{/* end inst-detail-sidebar */}
                </div>{/* end inst-detail-main */}
            </div>

            <MikaConfirmModal
                isOpen={dupConfirm}
                onClose={function() { setDupConfirm(false); }}
                onConfirm={function() { setDupConfirm(false); submitNow(); }}
                title={fm('service.form.dupTitle', 'Servisim ekzistues')}
                message={fm('service.form.dupMessage', 'Një ose më shumë produkte të zgjedhura kanë tashmë një servisim të hapur. Dëshironi të vazhdoni gjithsesi?')}
                confirmLabel={fm('service.form.dupConfirm', 'Vazhdo gjithsesi')}
                confirmColor="primary"
            />
        </div>
    );
};

var mapStateToProps = function(state: any) {
    return {
        projectsReadyForService: state.service.projectsReadyForService,
        loading: state.app.loading,
    };
};

var mapDispatchToProps = function(dispatch: any) {
    return {
        actions: bindActionCreators(serviceActions as any, dispatch),
    };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ServicingForm) as any) as any;
